import {
  GetMessageWithIntl,
  ModalDialogComponent,
  ModalDialogComponentProps,
} from '../..';
import { getDateFormat } from '~/shared/utils';
import { CaptionButton, IconWithCaptionButton } from '../Button';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { Textarea } from '..';
import './PreviewGenerateHistoryDialog.css';
import { IconButton } from '~/shared/components/ui/Button';

import { Radio, RadioItem } from '~/shared/components/parts/Radio/Radio';

export interface PreviewGenerateHistoryItem {
  updateDate?: Long.Long | null;
  prompt?: string;
  companyOverview?: string;
  evaluationIndex1?: string;
  evaluationIndex2?: string;
  evaluationIndex3?: string;
  evaluationIndex4?: string;
}
export interface PreviewGenerateInputOption {
  isPublic?: boolean;
  generateResults: PreviewGenerateHistoryItem[];
  publishedInfo?: PreviewGenerateHistoryItem;
  defaultSample?: PreviewGenerateHistoryItem;
  tempSave?: PreviewGenerateHistoryItem;
}

export interface PreviewGenerateHistoryDialogProps {
  isOpen: boolean;
  inputOption: PreviewGenerateInputOption;
  onDecision: (v: PreviewGenerateHistoryItem) => void;
  onCancel: () => void;
}

export const PreviewGenerateHistoryDialog = (
  props: PreviewGenerateHistoryDialogProps
) => {
  const intl = useIntl();

  const DialogTitle = 'PreviewGenerateHistoryDialog';

  const { inputOption } = props;
  const [historyItem, setHistoryItem] = useState<PreviewGenerateHistoryItem>(
    {}
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [isFirstPage, setFirstPage] = useState(false);
  const [isMaxPage, setMaxPage] = useState(false);

  const radioValues = ['generate', 'publicInfo', 'sample', 'tempSave'];
  const publicRadioItems: RadioItem[] = [
    {
      displayName: '生成結果',
      value: radioValues[0],
    },
    {
      displayName: '現在の公開情報',
      value: radioValues[1],
    },
    {
      displayName: '一時保存データ',
      value: radioValues[3],
    },
  ];
  const sampleRadioItems: RadioItem[] = [
    {
      displayName: '生成結果',
      value: radioValues[0],
    },
    {
      displayName: '初回サンプル',
      value: radioValues[2],
    },
    {
      displayName: '一時保存データ',
      value: radioValues[3],
    },
  ];
  const [radio, setRadio] = useState(radioValues[0]);
  const [readMore, setReadMore] = useState(false);

  useEffect(() => {
    setFirstPage(true);
    setMaxPage(inputOption.generateResults.length <= 1);
    setHistoryItem(inputOption.generateResults[0] ?? {});
    setRadio(radioValues[0]);
    setCurrentPage(1);
  }, [props.isOpen]);

  const handleCancel = () => {
    props.onCancel();
  };

  const handleDecision = (item: PreviewGenerateHistoryItem) => {
    props.onDecision(item);
  };

  const handleMovePage = (pageNumber: number) => {
    setFirstPage(pageNumber === 1);
    setCurrentPage(pageNumber);
    setMaxPage(inputOption.generateResults.length === pageNumber);
    setHistoryItem(inputOption.generateResults[pageNumber - 1] ?? {});
  };

  useEffect(() => {
    if (radio === radioValues[0]) {
      setHistoryItem(inputOption.generateResults[currentPage - 1] ?? {});
    } else if (radio === radioValues[1]) {
      setHistoryItem(inputOption.publishedInfo ?? {});
    } else if (radio === radioValues[2]) {
      setHistoryItem(inputOption.defaultSample ?? {});
    } else if (radio === radioValues[3]) {
      setHistoryItem(inputOption.tempSave ?? {});
    }
  }, [currentPage, inputOption.generateResults, radio]);

  const elements = (
    <div className="preview-generate-history-dialog">
      <div className="header">
        <Radio
          name="radioBtn"
          className="group"
          items={inputOption.isPublic ? publicRadioItems : sampleRadioItems}
          value={radio}
          onChangeState={setRadio}
        />
        <div className="moveNavi">
          <IconButton
            name=""
            iconType="prev"
            buttonType="basic"
            onClick={() => handleMovePage(currentPage - 1)}
            disabled={isFirstPage || radio !== radioValues[0]}
          />
          <IconButton
            name=""
            iconType="next"
            buttonType="basic"
            onClick={() => {
              handleMovePage(currentPage + 1);
            }}
            disabled={isMaxPage || radio !== radioValues[0]}
          />
        </div>
      </div>
      <div className="detail-area">
        <div className="input-line">
          <div className="item-group-100">
            <div className="w-75">
              {radio === radioValues[0] ? (
                <h4>
                  {`生成結果-${currentPage}` +
                    (historyItem.updateDate
                      ? ` (${getDateFormat(
                          historyItem.updateDate,
                          'YYYY/MM/DD HH:mm:ss'
                        )})`
                      : '')}
                </h4>
              ) : radio === radioValues[1] ? (
                <h4>
                  {`現在の公開情報` +
                    (historyItem.updateDate
                      ? ` (${getDateFormat(
                          historyItem.updateDate,
                          'YYYY/MM/DD HH:mm:ss'
                        )})`
                      : '')}
                </h4>
              ) : radio === radioValues[2] ? (
                <h4>
                  {`初回サンプル` +
                    (historyItem.updateDate
                      ? ` (${getDateFormat(
                          historyItem.updateDate,
                          'YYYY/MM/DD HH:mm:ss'
                        )})`
                      : '')}
                </h4>
              ) : (
                <h4>
                  {`一時保存データ` +
                    (historyItem.updateDate
                      ? ` (${getDateFormat(
                          historyItem.updateDate,
                          'YYYY/MM/DD HH:mm:ss'
                        )})`
                      : '')}
                </h4>
              )}
            </div>
          </div>
        </div>
        <div className="input-line">
          <div className="item-group-100">
            <div className="w-75">
              <Textarea
                name="prompt"
                className="w-100 mh-middle"
                labelId="PreviewGenerateHistoryDialog.prompt"
                value={historyItem.prompt ?? ''}
                columns={['prompt']}
                disabled={true}
              />
            </div>
          </div>
        </div>
        <div className="expandable-container-area">
          <div className="container-title">企業紹介</div>
          <div
            className={`expandable-container-text ${
              readMore ? 'is-expanded' : ''
            }`}
          >
            {historyItem.companyOverview ?? ''}
          </div>
          <IconWithCaptionButton
            name="readMore"
            iconType={readMore ? 'up' : 'down'}
            caption={readMore ? '詳細を閉じる' : 'もっと見る'}
            onClick={() => {
              readMore ? setReadMore(false) : setReadMore(true);
            }}
          />
        </div>
        <div className="description-container-area">
          <div className="description-container">
            <div className="container-title">得意な生産形態</div>
            <div className="container-text">
              {historyItem.evaluationIndex1 ?? ''}
            </div>
          </div>
          <div className="description-container">
            <div className="container-title">品質保証・検査</div>
            <div className="container-text">
              {historyItem.evaluationIndex2 ?? ''}
            </div>
          </div>
        </div>
        <div className="description-container-area">
          <div className="description-container">
            <div className="container-title">主要取引業界</div>
            <div className="container-text">
              {historyItem.evaluationIndex3 ?? ''}
            </div>
          </div>
          <div className="description-container">
            <div className="container-title">技術・設備のポイント</div>
            <div className="container-text">
              {historyItem.evaluationIndex4 ?? ''}
            </div>
          </div>
        </div>
      </div>
      <div className="button-area">
        <CaptionButton
          name="cancelBtn"
          buttonType="basic"
          className="button"
          caption={GetMessageWithIntl(intl, { id: 'close' })}
          onClick={handleCancel}
        />
        <CaptionButton
          name="cancelBtn"
          buttonType="basic"
          className="button"
          caption={GetMessageWithIntl(intl, {
            id: 'useThisVersion',
            prefixId: DialogTitle,
          })}
          onClick={() => handleDecision(historyItem)}
        />
      </div>
    </div>
  );

  const openModalProps: ModalDialogComponentProps = {
    cancel: handleCancel,
    send: () => {},
    modalIsOpen: props.isOpen,
    headerLabelId: {
      id: 'PREVIEW_GENERATE_HISTORY',
      prefixId: 'DIALOG_TITLE',
    },
    messageLabelId: {},
    elements,
  };
  return (
    <div className="PreviewGenerateHistoryDialog">
      <ModalDialogComponent {...openModalProps} />
    </div>
  );
};
